import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  CloudArrowUpIcon,
  CubeIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  MinusIcon,
  TableCellsIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { AlertCountBadge } from '@cottage/design-system/internal-tooling/ui'
import MultiSelectFilter from '@/components/MultiSelectFilter'
import type { MultiSelectOption } from '@/components/MultiSelectFilter'
import type { SortDirection } from '@/hooks/useSortableTable'
import { ColumnResizeHandle, HoverHelp, useColumnWidths } from '@/tableControls'
import SearchableSelect from '@/components/SearchableSelect'
import {
  buildBillingLedgerMatchOptions,
  buildProductLedgerMatchOptions,
  formatLedgerCaseCount,
} from '@/components/ledgerMatchPicker'
import { productInvoiceLinkBadges } from '@/components/productInvoiceLinkStatus'
import { matchesUploadsMonthFilter } from '@/components/uploadsMonthOptions'
import { productInvoiceErrorMonth } from '@/components/uploadsErrorCounts'
import { uploadsErrorVisibleForMonth } from '@/utils/completedCalendarMonth'
import { useSortableTable } from '@/hooks/useSortableTable'
import { toast } from '@/store/toastStore'
import {
  allocateWarehouseWhInvoiceShipping,
  createWarehouseProductInvoiceLine,
  deleteWarehouseProductInvoiceLine,
  ignoreWarehouseProductLine,
  linkWarehouseBillingLine,
  linkWarehouseProductLine,
  listWarehouseLedger,
  listWarehouseProductInvoiceLines,
  listWarehouseVendors,
  patchWarehouseProductInvoiceLine,
  setWarehouseProductInvoiceLineSku,
  setWarehouseProductLineQtyBasis,
  unlinkWarehouseProductLineTxn,
  uploadWarehouseBilling,
  uploadWarehouseInvoice,
  uploadWarehouseWhInvoice,
  type WarehouseInvoiceUpdatePayload,
} from '@/services/warehouse'
import type {
  WarehouseBillingAwarenessResponse,
  WarehouseBillingLineItem,
  WarehouseInvoiceSummary,
  WarehouseLedgerTransactionItem,
  WarehouseProductInvoiceLineItem,
  WarehouseProductInvoiceLinePatchRequest,
  WarehouseSkuCrosswalkItem,
  WarehouseVendorItem,
} from '@/services/warehouseTypes'
import { formatApiError } from '@/utils/apiError'
import { billingLedgerCandidateDateBounds } from '@/utils/isoDateMath'
import { formatCurrencyField, parseDistributionAmount } from '@/utils/waterfallCalculator'

const LEGACY_PACKAGE_CUTOFF = '2026-06'

const PACKAGE_MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]

const WH_TABLE_COLUMN_KEYS = [
  'month',
  'invoice_number',
  'invoice_date',
  'storage_fee_total',
  'admin_fee_total',
  'pick_fee_total',
  'receiving_fee_total',
  'freight',
  'total',
  'last_allocated',
  'allocate',
] as const

const WH_TABLE_DEFAULT_WIDTHS: Record<string, number> = {
  month: 104,
  invoice_number: 128,
  invoice_date: 112,
  storage_fee_total: 140,
  admin_fee_total: 132,
  pick_fee_total: 120,
  receiving_fee_total: 120,
  freight: 148,
  total: 156,
  last_allocated: 168,
  allocate: 108,
}

function whFixedColumnStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
  }
}

function WhInvoiceSortableHeader({
  label,
  sortKey,
  sortDirection,
  onSort,
  className = '',
  title,
  columnKey,
  width,
  onStartResize,
  style,
}: {
  label: ReactNode
  sortKey: string
  sortDirection: SortDirection | null
  onSort: (key: string) => void
  className?: string
  title?: string
  columnKey: string
  width: number
  onStartResize: (key: string, event: ReactMouseEvent) => void
  style?: CSSProperties
}) {
  const isSorted = sortDirection !== null
  const alignRight = className.includes('text-right')

  return (
    <th
      className={`sortable relative ${isSorted ? 'sorted' : ''} ${className}`}
      style={{
        ...style,
        ...whFixedColumnStyle(width),
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
      title={title}
      onClick={() => onSort(sortKey)}
    >
      <span
        className={`${
          alignRight ? 'flex w-full justify-end' : 'inline-flex'
        } items-center gap-1 pr-2`}
      >
        {label}
        <span className="sort-indicator">
          {sortDirection === 'asc' ? (
            <ChevronUpIcon className="h-3 w-3" />
          ) : sortDirection === 'desc' ? (
            <ChevronDownIcon className="h-3 w-3" />
          ) : (
            <ChevronUpDownIcon className="h-3 w-3" />
          )}
        </span>
      </span>
      <ColumnResizeHandle columnKey={columnKey} onStartResize={onStartResize} />
    </th>
  )
}

const COMPACT_INPUT =
  'h-8 box-border w-full min-w-0 rounded border border-gray-200 bg-white px-1.5 py-1 text-left text-sm'

const COMPACT_MONEY_INPUT =
  'h-8 box-border w-full min-w-0 rounded border border-gray-200 bg-white px-1.5 py-1 text-right text-sm tabular-nums'

const READONLY_CELL = 'flex min-h-8 items-center'

function formatMoney(value: number | string | null | undefined): string {
  if (value == null || value === '') return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function formatQuantity(value: number | string | null | undefined): string {
  if (value == null || value === '') return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function packageYearOptions(): { value: string; label: string }[] {
  const current = new Date().getFullYear()
  return [current - 1, current, current + 1].map((year) => ({
    value: String(year),
    label: String(year),
  }))
}

function productLineLinkedTxnIds(line: WarehouseProductInvoiceLineItem): string[] {
  if (line.linked_bk_txn_ids?.length) return line.linked_bk_txn_ids
  if (line.linked_bk_txn_id) return [line.linked_bk_txn_id]
  return []
}

function productLineOverlayPatch(line: WarehouseProductInvoiceLineItem) {
  return {
    ignored: Boolean(line.ignored),
    linked_bk_txn_id: line.linked_bk_txn_id ?? null,
    linked_bk_txn_ids: productLineLinkedTxnIds(line),
    ignore_reason: line.ignore_reason ?? null,
  }
}

const IGNORE_REASON_PRESETS = ['Store-direct', 'Not warehouse SKU', 'Other'] as const

const LINKABLE_PRODUCT_LEDGER_KINDS = new Set([
  'manufacturer_purchase',
  'manual_adjustment',
])

function shortTxnId(bkTxnId: string | null | undefined): string {
  if (!bkTxnId) return '—'
  return bkTxnId.slice(0, 8)
}

type AddEditFilesView = 'chooser' | 'wh' | 'product' | 'billing' | 'manage'

function formatUploadedAt(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
}

function formatLastAllocated(value: string | null | undefined): string {
  if (!value) return 'Never'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

function PanelCard({
  step,
  title,
  description,
  children,
  headerAction,
  errorCount = 0,
}: {
  step: number
  title: string
  description: string
  children: ReactNode
  headerAction?: ReactNode
  errorCount?: number
}) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-gray-200 px-4 py-3 md:px-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
              style={{ backgroundColor: 'var(--color-brand-600)' }}
            >
              {step}
            </span>
            <h2 className="container-title">{title}</h2>
            <AlertCountBadge
              count={errorCount}
              label={`${errorCount} error${errorCount === 1 ? '' : 's'}`}
              variant="section"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">{description}</p>
        </div>
        {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
      </div>
      <div className="px-4 py-4 md:px-5">{children}</div>
    </section>
  )
}

function toMoneyEditDraft(value: number): string {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return n.toFixed(2)
}

function EditableMoneyCell({
  value,
  disabled,
  onCommit,
}: {
  value: number
  disabled?: boolean
  onCommit: (next: number) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  async function saveDraft() {
    const parsed = parseDistributionAmount(draft)
    if (parsed == null) {
      setEditing(false)
      return
    }
    if (Math.abs(parsed - Number(value)) < 0.005) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onCommit(parsed)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  if (disabled) return <span className="block text-right tabular-nums">{formatMoney(value)}</span>

  return (
    <input
      type="text"
      inputMode="decimal"
      className={COMPACT_MONEY_INPUT}
      value={editing ? draft : formatCurrencyField(value) || formatMoney(value)}
      disabled={saving}
      onFocus={(e) => {
        setDraft(toMoneyEditDraft(value))
        setEditing(true)
        e.currentTarget.select()
      }}
      onChange={(e) => {
        setEditing(true)
        setDraft(e.target.value)
      }}
      onBlur={() => {
        if (editing) void saveDraft()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          void saveDraft()
        }
      }}
    />
  )
}

function EditableNullableMoneyCell({
  value,
  disabled,
  onCommit,
  allowClear = false,
}: {
  value: number | null | undefined
  disabled?: boolean
  allowClear?: boolean
  onCommit: (next: number | null) => Promise<void>
}) {
  const hasValue = value != null && Number.isFinite(Number(value))
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  async function saveDraft() {
    const trimmed = draft.trim()
    if (trimmed === '' && allowClear) {
      if (!hasValue) {
        setEditing(false)
        return
      }
      setSaving(true)
      try {
        await onCommit(null)
        setEditing(false)
      } finally {
        setSaving(false)
      }
      return
    }
    const parsed = parseDistributionAmount(draft)
    if (parsed == null) {
      setEditing(false)
      return
    }
    if (hasValue && Math.abs(parsed - Number(value)) < 0.005) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onCommit(parsed)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  if (disabled) {
    return hasValue ? (
      <span className="block text-right tabular-nums">{formatMoney(value)}</span>
    ) : (
      <span className="block text-right text-gray-300">—</span>
    )
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      className={COMPACT_MONEY_INPUT}
      value={
        editing
          ? draft
          : hasValue
            ? formatCurrencyField(value) || formatMoney(value)
            : ''
      }
      disabled={saving}
      placeholder="—"
      onFocus={(e) => {
        setDraft(hasValue ? toMoneyEditDraft(Number(value)) : '')
        setEditing(true)
        e.currentTarget.select()
      }}
      onChange={(e) => {
        setEditing(true)
        setDraft(e.target.value)
      }}
      onBlur={() => {
        if (editing) void saveDraft()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          void saveDraft()
        }
      }}
    />
  )
}

function EditableNullableQuantityCell({
  value,
  disabled,
  onCommit,
}: {
  value: number | null | undefined
  disabled?: boolean
  onCommit: (next: number) => Promise<void>
}) {
  const hasValue = value != null && Number.isFinite(Number(value))
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  async function saveDraft() {
    const parsed = parseDistributionAmount(draft)
    if (parsed == null) {
      setEditing(false)
      return
    }
    if (hasValue && Math.abs(parsed - Number(value)) < 0.005) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onCommit(parsed)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  if (disabled) {
    return hasValue ? <span>{formatQuantity(value)}</span> : <span className="text-gray-300">—</span>
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      className={`${COMPACT_INPUT} tabular-nums`}
      value={editing ? draft : hasValue ? formatQuantity(value) : ''}
      disabled={saving}
      placeholder="—"
      onFocus={() => {
        setDraft(hasValue ? formatQuantity(value) : '')
        setEditing(true)
      }}
      onChange={(e) => {
        setEditing(true)
        setDraft(e.target.value)
      }}
      onBlur={() => {
        if (editing) void saveDraft()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          void saveDraft()
        }
      }}
    />
  )
}

function formatAllocationDelta(
  value: number | null | undefined,
  format: 'money' | 'quantity'
): string | null {
  if (value == null || !Number.isFinite(Number(value))) return null
  const num = Number(value)
  if (Math.abs(num) < 0.005) return null
  const sign = num > 0 ? '+' : ''
  if (format === 'money') return `${sign}${formatMoney(num)}`
  return `${sign}${formatQuantity(num)}`
}

function formatLandedCost(value: number | string | null | undefined): string {
  if (value == null || value === '') return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
}

function formatProductLineExtended(line: WarehouseProductInvoiceLineItem): string {
  const ppc = line.price_per_case
  if (ppc == null) {
    return '—'
  }
  const cases = line.case_count
  if (
    cases != null &&
    Number.isFinite(Number(cases)) &&
    Number.isFinite(Number(ppc))
  ) {
    return formatMoney(Number(cases) * Number(ppc))
  }
  const fallback = line.extended_price ?? line.amount
  if (fallback != null && Number.isFinite(Number(fallback))) {
    return formatMoney(fallback)
  }
  return '—'
}

function productLineMoneyOptimistic(
  line: WarehouseProductInvoiceLineItem,
  patch: Pick<WarehouseProductInvoiceLinePatchRequest, 'case_count' | 'price_per_case'>
): Partial<WarehouseProductInvoiceLineItem> {
  const optimistic: Partial<WarehouseProductInvoiceLineItem> = { ...patch }
  const cases = patch.case_count ?? line.case_count
  const ppc =
    patch.price_per_case !== undefined ? patch.price_per_case : line.price_per_case
  if (ppc == null) {
    optimistic.extended_price = 0
    optimistic.amount = 0
  } else if (
    cases != null &&
    Number.isFinite(Number(cases)) &&
    Number.isFinite(Number(ppc))
  ) {
    const extended = Number(cases) * Number(ppc)
    optimistic.extended_price = extended
    optimistic.amount = extended
  }
  return optimistic
}

function ProductInvoiceAllocationBanner({
  invoice,
  productLines,
}: {
  invoice: WarehouseInvoiceSummary
  productLines: WarehouseProductInvoiceLineItem[]
}) {
  if (!uploadsErrorVisibleForMonth(productInvoiceErrorMonth(invoice))) {
    return null
  }
  if (invoice.allocation_valid === false) {
    const extendedDelta = formatAllocationDelta(invoice.allocation_extended_delta, 'money')

    return (
      <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        <div className="flex items-start gap-1.5">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
          <div>
            <p className="font-medium">
              Line extended total must match the extracted invoice before linking.
            </p>
            {extendedDelta ? (
              <p className="mt-0.5">
                Difference: extended {extendedDelta}
              </p>
            ) : null}
            {invoice.parsed_product_extended != null ? (
              <p className="mt-0.5 text-amber-800">
                Invoice extended total: {formatMoney(invoice.parsed_product_extended)}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  if (invoice.allocation_valid === true) {
    const hasManualLine = productLines.some((line) => line.is_manual)
    if (!hasManualLine && productLines.length <= 1) return null
    return (
      <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
        <p>Allocation matches invoice extended total.</p>
      </div>
    )
  }

  return null
}

function EditableTextCell({
  value,
  disabled,
  onCommit,
}: {
  value: string
  disabled?: boolean
  onCommit: (next: string) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  async function saveDraft() {
    const next = draft.trim()
    if (!next || next === value) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onCommit(next)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  if (disabled) return <span>{value || '—'}</span>

  return (
    <input
      type="text"
      className={COMPACT_INPUT}
      value={editing ? draft : value}
      disabled={saving}
      onFocus={() => {
        setDraft(value)
        setEditing(true)
      }}
      onChange={(e) => {
        setEditing(true)
        setDraft(e.target.value)
      }}
      onBlur={() => {
        if (editing) void saveDraft()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          void saveDraft()
        }
      }}
    />
  )
}

function EditableDateCell({
  value,
  disabled,
  onCommit,
}: {
  value: string | null | undefined
  disabled?: boolean
  onCommit: (next: string) => Promise<void>
}) {
  const resolved = value ?? ''
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  async function saveDraft() {
    if (!draft || draft === resolved) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onCommit(draft)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  if (disabled) {
    return <span className={resolved ? undefined : 'text-gray-300'}>{resolved || '—'}</span>
  }

  return (
    <input
      type={editing ? 'date' : 'text'}
      className={`${COMPACT_INPUT} tabular-nums`}
      value={editing ? draft : resolved}
      disabled={saving}
      onFocus={() => {
        setDraft(resolved)
        setEditing(true)
      }}
      onChange={(e) => {
        setEditing(true)
        setDraft(e.target.value)
      }}
      onBlur={() => {
        if (editing) void saveDraft()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          void saveDraft()
        }
      }}
    />
  )
}

function LinkIgnoreSegment({
  mode,
  disabled,
  onModeChange,
}: {
  mode: 'link' | 'ignore'
  disabled?: boolean
  onModeChange: (mode: 'link' | 'ignore') => void
}) {
  return (
    <div className="inline-flex shrink-0 rounded border border-gray-200 bg-gray-50 p-0.5">
      <button
        type="button"
        className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
          mode === 'link'
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        }`}
        disabled={disabled}
        onClick={() => onModeChange('link')}
      >
        Link
      </button>
      <button
        type="button"
        className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
          mode === 'ignore'
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        }`}
        disabled={disabled}
        onClick={() => onModeChange('ignore')}
      >
        Ignore
      </button>
    </div>
  )
}

function ProductLineQtyBasisControl({
  line,
  disabled,
  onBasisChange,
}: {
  line: WarehouseProductInvoiceLineItem
  disabled?: boolean
  onBasisChange: (qtyBasis: 'units' | 'cases' | 'auto') => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const resolved = line.resolved_qty_basis ?? (line.unit_count === line.quantity ? 'units' : 'cases')
  const isManual = line.qty_basis === 'units' || line.qty_basis === 'cases'
  const flipTarget: 'units' | 'cases' = resolved === 'units' ? 'cases' : 'units'
  const label = resolved === 'units' ? 'As units' : 'As cases'

  return (
    <div className="flex flex-wrap items-center gap-1 text-xs leading-tight text-gray-500">
      <span>
        {label}
        {isManual ? <span className="text-gray-400"> · Manual</span> : null}
      </span>
      <button
        type="button"
        className="text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
        disabled={disabled || busy}
        onClick={() => {
          setBusy(true)
          void onBasisChange(flipTarget).finally(() => setBusy(false))
        }}
      >
        → {flipTarget}
      </button>
      {isManual ? (
        <button
          type="button"
          className="text-gray-400 hover:text-gray-600 hover:underline disabled:opacity-50"
          disabled={disabled || busy}
          onClick={() => {
            setBusy(true)
            void onBasisChange('auto').finally(() => setBusy(false))
          }}
        >
          Auto
        </button>
      ) : null}
    </div>
  )
}

function ProductLineSkuSelect({
  options,
  value,
  disabled,
  onSelect,
}: {
  options: { id: string; label: string }[]
  value?: string | null
  disabled?: boolean
  onSelect: (skuId: string) => Promise<void>
}) {
  const [selecting, setSelecting] = useState(false)

  return (
    <SearchableSelect
      options={options}
      value={value ?? ''}
      onChange={(id) => {
        if (!id || id === value) return
        setSelecting(true)
        void onSelect(id).finally(() => setSelecting(false))
      }}
      disabled={disabled || selecting || options.length === 0}
      size="compact"
      preserveOrder
      preferBelow
      useViewportForPlacement
      className="min-w-[16rem]"
      placeholder="Pick catalog SKU…"
    />
  )
}

function InvoiceVendorSelect({
  options,
  value,
  disabled,
  onSelect,
}: {
  options: { id: string; label: string }[]
  value?: number | null
  disabled?: boolean
  onSelect: (vendorId: number) => Promise<void>
}) {
  const [selecting, setSelecting] = useState(false)
  const valueKey = value != null ? String(value) : ''

  return (
    <SearchableSelect
      options={options}
      value={valueKey}
      onChange={(id) => {
        if (!id) return
        const vendorId = Number(id)
        if (!Number.isFinite(vendorId) || vendorId === value) return
        setSelecting(true)
        void onSelect(vendorId).finally(() => setSelecting(false))
      }}
      disabled={disabled || selecting || options.length === 0}
      size="compact"
      preserveOrder
      preferBelow
      useViewportForPlacement
      className="min-w-[14rem]"
      placeholder="Select vendor…"
    />
  )
}

function IgnoreReasonPopover({
  anchorRef,
  onConfirm,
  onCancel,
}: {
  anchorRef: RefObject<HTMLElement | null>
  onConfirm: (reason: string) => void
  onCancel: () => void
}) {
  const [custom, setCustom] = useState('')
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [panelBox, setPanelBox] = useState<{
    top?: number
    bottom?: number
    left: number
    width: number
  } | null>(null)

  const resolvedReason =
    selectedPreset === 'Other'
      ? custom.trim()
      : selectedPreset ?? custom.trim()

  useLayoutEffect(() => {
    function compute() {
      const anchor = anchorRef.current
      if (!anchor) return
      const rect = anchor.getBoundingClientRect()
      const padding = 8
      const width = Math.min(224, window.innerWidth - padding * 2)
      let left = rect.left
      left = Math.max(padding, Math.min(left, window.innerWidth - width - padding))
      const spaceBelow = window.innerHeight - rect.bottom - padding
      const estimatedHeight = 180
      const openAbove = spaceBelow < estimatedHeight && rect.top > spaceBelow
      setPanelBox(
        openAbove
          ? { bottom: window.innerHeight - rect.top + 4, left, width }
          : { top: rect.bottom + 4, left, width }
      )
    }
    compute()
    window.addEventListener('resize', compute)
    window.addEventListener('scroll', compute, true)
    return () => {
      window.removeEventListener('resize', compute)
      window.removeEventListener('scroll', compute, true)
    }
  }, [anchorRef])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel()
    }
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node
      if (panelRef.current?.contains(target)) return
      if (anchorRef.current?.contains(target)) return
      onCancel()
    }
    window.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [anchorRef, onCancel])

  if (!panelBox || typeof document === 'undefined') return null

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Ignore reason"
      style={{
        position: 'fixed',
        top: panelBox.top,
        bottom: panelBox.bottom,
        left: panelBox.left,
        width: panelBox.width,
        zIndex: 80,
      }}
      className="rounded-md border border-gray-200 bg-white p-2 shadow-lg"
    >
      <p className="text-[11px] font-medium text-gray-700">Ignore reason</p>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {IGNORE_REASON_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            className={`rounded px-2 py-0.5 text-[11px] ${
              selectedPreset === preset
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setSelectedPreset(preset)}
          >
            {preset}
          </button>
        ))}
      </div>
      {selectedPreset === 'Other' || !selectedPreset ? (
        <input
          type="text"
          className="mt-2 w-full rounded border border-gray-200 px-2 py-1 text-xs"
          placeholder="Optional note…"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
        />
      ) : null}
      <div className="mt-2 flex justify-end gap-1.5">
        <button
          type="button"
          className="rounded px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rounded bg-gray-900 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-50"
          disabled={!resolvedReason}
          onClick={() => onConfirm(resolvedReason)}
        >
          Ignore
        </button>
      </div>
    </div>,
    document.body
  )
}

function ProductLineLinkPicker({
  lineId,
  candidates,
  disabled,
  onLink,
}: {
  lineId: number
  candidates: WarehouseLedgerTransactionItem[]
  disabled?: boolean
  onLink: (bkTxnId: string) => Promise<void>
}) {
  const options = useMemo(
    () => buildProductLedgerMatchOptions(candidates, lineId),
    [candidates, lineId]
  )
  const [selected, setSelected] = useState('')
  const [linking, setLinking] = useState(false)

  return (
    <SearchableSelect
      options={options}
      value={selected}
      onChange={(id) => {
        if (!id) {
          setSelected('')
          return
        }
        setSelected(id)
        setLinking(true)
        void onLink(id).finally(() => {
          setLinking(false)
          setSelected('')
        })
      }}
      disabled={disabled || linking || options.length === 0}
      size="compact"
      preserveOrder
      preferBelow
      useViewportForPlacement
      className="min-w-[28rem]"
      placeholder={options.length === 0 ? 'No ledger candidates' : 'Link ledger transaction'}
    />
  )
}

function ledgerTxnHoverText(
  txn: WarehouseLedgerTransactionItem | undefined,
  opts?: { jumpHint?: boolean }
): string {
  const lines = [
    `Date: ${txn?.txn_date || '—'}`,
    `SKU: ${txn?.bk_sku || '—'}`,
    `Product: ${(txn?.product_name || '').trim() || '—'}`,
    `Cases: ${txn ? formatLedgerCaseCount(txn.signed_cases) : '—'}`,
  ]
  const note = txn?.notes?.trim()
  if (note) lines.push(`Note: ${note}`)
  if (opts?.jumpHint) lines.push('Click to open in Ledger')
  return lines.join('\n')
}

function ProductLineMismatchHints({
  line,
  linkedTxnCostsById,
}: {
  line: WarehouseProductInvoiceLineItem
  linkedTxnCostsById: Map<string, number | null>
}) {
  if (!productLineLinkedTxnIds(line).length) return null

  const invoiceCost = line.landed_price_per_case ?? line.price_per_case ?? line.unit_price
  const linkedCosts = productLineLinkedTxnIds(line)
    .map((txnId) => linkedTxnCostsById.get(txnId))
    .filter((cost): cost is number => cost != null && Number.isFinite(cost))
    .map((cost) => formatMoney(cost))

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {line.qty_mismatch ? (
        <HoverHelp
          label={
            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
              Qty differs
            </span>
          }
          aria-label="Quantity mismatch details"
          className="inline-flex"
        >
          <p>
            Invoice {formatQuantity(line.case_count)} cases vs linked{' '}
            {formatQuantity(line.linked_cases_total)} cases
          </p>
        </HoverHelp>
      ) : null}
      {line.cost_mismatch ? (
        <HoverHelp
          label={
            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
              Cost differs
            </span>
          }
          aria-label="Cost mismatch details"
          className="inline-flex"
        >
          <p>
            Invoice landed {formatLandedCost(invoiceCost)}/case vs ledger{' '}
            {linkedCosts.length > 0 ? linkedCosts.join(', ') : '—'}
          </p>
        </HoverHelp>
      ) : null}
    </span>
  )
}

function ProductLineLinkedTxnChips({
  txnIds,
  disabled,
  linkedTxnsById,
  onUnlinkTxn,
  onJumpToLedgerTxn,
}: {
  txnIds: string[]
  disabled?: boolean
  linkedTxnsById: Map<string, WarehouseLedgerTransactionItem>
  onUnlinkTxn: (bkTxnId: string) => void
  onJumpToLedgerTxn?: (bkTxnId: string) => void
}) {
  return (
    <span className="inline-flex max-w-full flex-wrap items-center gap-1">
      {txnIds.map((txnId) => {
        const txn = linkedTxnsById.get(txnId)
        const hoverText = ledgerTxnHoverText(txn, { jumpHint: Boolean(onJumpToLedgerTxn) })

        return (
          <span
            key={txnId}
            role={onJumpToLedgerTxn ? 'button' : undefined}
            tabIndex={onJumpToLedgerTxn && !disabled ? 0 : undefined}
            title={hoverText}
            className={`group inline-flex max-w-full items-center gap-0.5 rounded bg-emerald-50 py-0.5 pl-2 pr-1 text-xs text-emerald-800 ${
              onJumpToLedgerTxn ? 'cursor-pointer hover:bg-emerald-100' : undefined
            }`}
            onClick={() => {
              if (disabled || !onJumpToLedgerTxn) return
              onJumpToLedgerTxn(txnId)
            }}
            onKeyDown={(e) => {
              if (disabled || !onJumpToLedgerTxn) return
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onJumpToLedgerTxn(txnId)
              }
            }}
          >
            {shortTxnId(txnId)}
            <button
              type="button"
              className="rounded p-0.5 text-emerald-700 opacity-60 hover:bg-emerald-100 hover:opacity-100 disabled:opacity-40"
              aria-label={`Unlink ${txnId}`}
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation()
                onUnlinkTxn(txnId)
              }}
            >
              <XMarkIcon className="h-3 w-3" aria-hidden="true" />
            </button>
          </span>
        )
      })}
    </span>
  )
}
function ProductLineMatchControls({
  line,
  candidates,
  disabled,
  onIgnore,
  onLink,
}: {
  line: WarehouseProductInvoiceLineItem
  candidates: WarehouseLedgerTransactionItem[]
  disabled?: boolean
  onIgnore: (ignored: boolean, ignoreReason?: string | null) => Promise<void>
  onLink: (bkTxnId: string) => Promise<void>
}) {
  const [modeBusy, setModeBusy] = useState(false)
  const [ignorePopoverOpen, setIgnorePopoverOpen] = useState(false)
  const segmentRef = useRef<HTMLDivElement>(null)
  const mode: 'link' | 'ignore' = line.ignored ? 'ignore' : 'link'
  const controlsDisabled = disabled || modeBusy

  function handleModeChange(next: 'link' | 'ignore') {
    if (next === mode) return
    if (next === 'ignore') {
      setIgnorePopoverOpen(true)
      return
    }
    setModeBusy(true)
    void onIgnore(false).finally(() => setModeBusy(false))
  }

  return (
    <div className="relative flex flex-wrap items-center gap-1.5">
      <div ref={segmentRef}>
        <LinkIgnoreSegment
          mode={mode}
          disabled={controlsDisabled}
          onModeChange={handleModeChange}
        />
      </div>
      {mode === 'link' ? (
        <ProductLineLinkPicker
          lineId={line.id}
          candidates={candidates}
          disabled={controlsDisabled}
          onLink={onLink}
        />
      ) : null}
      {ignorePopoverOpen ? (
        <IgnoreReasonPopover
          anchorRef={segmentRef}
          onCancel={() => setIgnorePopoverOpen(false)}
          onConfirm={(reason) => {
            setIgnorePopoverOpen(false)
            setModeBusy(true)
            void onIgnore(true, reason).finally(() => setModeBusy(false))
          }}
        />
      ) : null}
    </div>
  )
}

function BillingMatchPicker({
  candidates,
  disabled,
  onLink,
}: {
  candidates: WarehouseLedgerTransactionItem[]
  disabled?: boolean
  onLink: (bkTxnId: string) => Promise<void>
}) {
  const options = useMemo(() => buildBillingLedgerMatchOptions(candidates), [candidates])
  const [linking, setLinking] = useState(false)

  if (options.length === 0) {
    return <span className="text-xs text-gray-500">No ledger candidates</span>
  }

  return (
    <SearchableSelect
      options={options}
      value=""
      onChange={(id) => {
        if (!id) return
        setLinking(true)
        void onLink(id).finally(() => setLinking(false))
      }}
      disabled={disabled || linking}
      size="compact"
      preserveOrder
      preferBelow
      useViewportForPlacement
      className="min-w-[28rem]"
      placeholder="Pick ledger txn…"
    />
  )
}

function billingQtyCheckLabel(
  aware: {
    qty_mismatch?: boolean
    message?: string | null
    billing_qty?: number | string | null
    ledger_qty?: number | string | null
  } | undefined,
  reportMonth: string
): ReactNode {
  if (!aware) return '—'
  if (aware.qty_mismatch && uploadsErrorVisibleForMonth(reportMonth)) {
    return (
      <span className="inline-flex items-center gap-1 text-red-800">
        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
        Billing {aware.billing_qty} vs ledger {aware.ledger_qty ?? '—'}
      </span>
    )
  }
  if (aware.message === 'ok') {
    return <span className="text-gray-600">Match</span>
  }
  if (aware.message === 'no_ledger_match') {
    return <span className="text-gray-600">No ledger match</span>
  }
  if (aware.message) {
    return <span className="text-gray-600">{aware.message.replaceAll('_', ' ')}</span>
  }
  return '—'
}

function BillingLinkedTxnChip({
  bkTxnId,
  txn,
  disabled,
  onJumpToLedgerTxn,
}: {
  bkTxnId: string
  txn?: WarehouseLedgerTransactionItem
  disabled?: boolean
  onJumpToLedgerTxn?: (bkTxnId: string) => void
}) {
  const canJump = Boolean(onJumpToLedgerTxn) && !disabled
  const label = shortTxnId(bkTxnId)
  const chipClass =
    'inline-flex max-w-full min-w-0 items-center rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800 ring-1 ring-emerald-100'
  const hoverText = ledgerTxnHoverText(txn, { jumpHint: canJump })

  return canJump ? (
    <button
      type="button"
      className={`${chipClass} hover:bg-emerald-100`}
      title={hoverText}
      aria-label={`Open ledger transaction ${bkTxnId} in Ledger`}
      onClick={() => onJumpToLedgerTxn?.(bkTxnId)}
    >
      <span className="min-w-0 truncate font-mono">{label}</span>
    </button>
  ) : (
    <span className={chipClass} title={hoverText}>
      <span className="min-w-0 truncate font-mono">{label}</span>
    </span>
  )
}

export type WarehouseUploadsPanelProps = {
  loading: boolean
  invoices: WarehouseInvoiceSummary[]
  billingLines: WarehouseBillingLineItem[]
  awareness: WarehouseBillingAwarenessResponse | null
  catalogSkuOptions: WarehouseSkuCrosswalkItem[]
  selectedMonths: string[]
  onSelectedMonthsChange: (months: string[]) => void
  monthOptions: MultiSelectOption[]
  syncAction?: ReactNode
  containerErrorCounts?: { wh: number; product: number; billing: number }
  focusProductInvoiceLineRequest?: { id: number; nonce: number } | null
  onFocusProductInvoiceHandled?: () => void
  onRefresh: () => Promise<void>
  onOpenCatalogSku?: (sku: string) => void
  onJumpToLedgerTxn?: (bkTxnId: string) => void
  onInvoiceFieldCommit: (
    invoiceId: number,
    payload: WarehouseInvoiceUpdatePayload
  ) => Promise<void>
  onDeleteInvoice: (invoiceId: number) => Promise<void>
  onDeleteBillingMonth: (reportMonth: string) => Promise<void>
}

export default function WarehouseUploadsPanel({
  loading,
  invoices,
  billingLines,
  awareness,
  catalogSkuOptions,
  selectedMonths,
  onSelectedMonthsChange,
  monthOptions,
  syncAction,
  containerErrorCounts,
  focusProductInvoiceLineRequest,
  onFocusProductInvoiceHandled,
  onRefresh,
  onOpenCatalogSku,
  onJumpToLedgerTxn,
  onInvoiceFieldCommit,
  onDeleteInvoice,
  onDeleteBillingMonth,
}: WarehouseUploadsPanelProps) {
  const [busy, setBusy] = useState(false)
  const [allocatingInvoiceId, setAllocatingInvoiceId] = useState<number | null>(null)

  const [whUploadYear, setWhUploadYear] = useState('')
  const [whUploadMonthPart, setWhUploadMonthPart] = useState('')
  const [whUploadFile, setWhUploadFile] = useState<File | null>(null)
  const whUploadMonth =
    whUploadYear && whUploadMonthPart ? `${whUploadYear}-${whUploadMonthPart}` : ''

  const [billingYear, setBillingYear] = useState('')
  const [billingMonthPart, setBillingMonthPart] = useState('')
  const [billingFile, setBillingFile] = useState<File | null>(null)
  const [legacyUpload, setLegacyUpload] = useState(false)
  const billingMonth =
    billingYear && billingMonthPart ? `${billingYear}-${billingMonthPart}` : ''

  const [addEditFilesOpen, setAddEditFilesOpen] = useState(false)
  const [addEditFilesView, setAddEditFilesView] = useState<AddEditFilesView>('chooser')

  const [expandedProductInvoiceId, setExpandedProductInvoiceId] = useState<number | null>(null)
  const [productLines, setProductLines] = useState<WarehouseProductInvoiceLineItem[]>([])
  const [productLinesLoading, setProductLinesLoading] = useState(false)
  const [productLineSaving, setProductLineSaving] = useState(false)
  /** Local link/ignore patches so invoice badges update before onRefresh lands. */
  const [productLineLinkOverlay, setProductLineLinkOverlay] = useState(
    {} as Record<
      number,
      {
        ignored: boolean
        linked_bk_txn_id: string | null
        linked_bk_txn_ids?: string[]
        ignore_reason?: string | null
      }
    >
  )
  const [productLedgerCandidates, setProductLedgerCandidates] = useState<
    WarehouseLedgerTransactionItem[]
  >([])
  const [billingLedgerCandidates, setBillingLedgerCandidates] = useState<
    WarehouseLedgerTransactionItem[]
  >([])
  /** Local link patches so billing rows update before onRefresh lands. */
  const [billingLineLinkOverlay, setBillingLineLinkOverlay] = useState(
    {} as Record<number, { linked_bk_txn_id: string }>
  )

  const [vendors, setVendors] = useState<WarehouseVendorItem[]>([])
  const [vendorsLoading, setVendorsLoading] = useState(false)
  const [productUploadVendorId, setProductUploadVendorId] = useState('')
  const [productUploadFiles, setProductUploadFiles] = useState<File[]>([])
  const [productUploadDragging, setProductUploadDragging] = useState(false)

  const productInputRef = useRef<HTMLInputElement>(null)
  const whInputRef = useRef<HTMLInputElement>(null)
  const productInvoiceRowRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const productLinesFetchGen = useRef(0)

  const whInvoices = useMemo(
    () =>
      invoices
        .filter(
          (invoice) =>
            invoice.invoice_type === 'wh_service' ||
            (invoice.invoice_type === 'mixed' && invoice.report_month)
        )
        .filter((invoice) => matchesUploadsMonthFilter(invoice.report_month, selectedMonths))
        .slice()
        .sort((a, b) => (a.report_month ?? '').localeCompare(b.report_month ?? '')),
    [invoices, selectedMonths]
  )

  const productInvoices = useMemo(
    () =>
      invoices
        .filter((invoice) => {
          if (invoice.invoice_type === 'wh_service') return false
          return !(invoice.report_month && invoice.invoice_type === 'mixed')
        })
        .filter((invoice) =>
          matchesUploadsMonthFilter(invoice.invoice_date?.slice(0, 7), selectedMonths)
        )
        .slice()
        .sort((a, b) => (a.invoice_date ?? '').localeCompare(b.invoice_date ?? '')),
    [invoices, selectedMonths]
  )

  const manageWhInvoices = useMemo(
    () =>
      invoices
        .filter(
          (invoice) =>
            invoice.invoice_type === 'wh_service' ||
            (invoice.invoice_type === 'mixed' && invoice.report_month)
        )
        .slice()
        .sort((a, b) => (b.uploaded_at ?? '').localeCompare(a.uploaded_at ?? '')),
    [invoices]
  )

  const manageProductInvoices = useMemo(
    () =>
      invoices
        .filter((invoice) => {
          if (invoice.invoice_type === 'wh_service') return false
          return !(invoice.report_month && invoice.invoice_type === 'mixed')
        })
        .slice()
        .sort((a, b) => (b.uploaded_at ?? '').localeCompare(a.uploaded_at ?? '')),
    [invoices]
  )

  const manageBillingMonths = useMemo(() => {
    const byMonth = new Map<string, { source_filename?: string | null }>()
    for (const line of billingLines) {
      const existing = byMonth.get(line.report_month)
      if (!existing) {
        byMonth.set(line.report_month, { source_filename: line.source_filename })
      } else if (line.source_filename && !existing.source_filename) {
        existing.source_filename = line.source_filename
      }
    }
    return Array.from(byMonth.entries())
      .map(([report_month, meta]) => ({
        report_month,
        source_filename: meta.source_filename ?? null,
      }))
      .sort((a, b) => b.report_month.localeCompare(a.report_month))
  }, [billingLines])

  const catalogSkuSelectOptions = useMemo(
    () =>
      catalogSkuOptions
        .filter((row) => row.is_active && row.bk_sku.trim())
        .map((row) => ({
          id: row.bk_sku,
          label: `${row.bk_sku} — ${row.display_name}`,
        }))
        .sort((a, b) => a.id.localeCompare(b.id)),
    [catalogSkuOptions]
  )

  const vendorSelectOptions = useMemo(
    () =>
      vendors
        .map((vendor) => ({
          id: String(vendor.id),
          label: vendor.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [vendors]
  )

  const productUploadSelectedVendor = useMemo(
    () => vendors.find((vendor) => String(vendor.id) === productUploadVendorId),
    [vendors, productUploadVendorId]
  )

  const productUploadVendorHasParser = productUploadSelectedVendor?.has_invoice_parser ?? false

  const billingLinesFiltered = useMemo(() => {
    return billingLines
      .filter((line) => matchesUploadsMonthFilter(line.report_month, selectedMonths))
      .slice()
      .sort((a, b) => {
        const monthCompare = a.report_month.localeCompare(b.report_month)
        if (monthCompare !== 0) return monthCompare
        return a.order_id.localeCompare(b.order_id)
      })
  }, [billingLines, selectedMonths])

  const billingLinesDisplay = useMemo(
    () =>
      billingLinesFiltered.map((line) => ({
        ...line,
        ...(billingLineLinkOverlay[line.id] ?? {}),
      })),
    [billingLinesFiltered, billingLineLinkOverlay]
  )

  const activeBillingMonths = useMemo(() => {
    const months = new Set(billingLinesFiltered.map((line) => line.report_month))
    return Array.from(months).sort()
  }, [billingLinesFiltered])

  const whErrorCount = containerErrorCounts?.wh ?? 0
  const productErrorCount = containerErrorCounts?.product ?? 0
  const billingErrorCount = containerErrorCounts?.billing ?? 0

  const {
    sortedData: sortedWhInvoices,
    requestSort: requestWhSort,
    getSortDirection: getWhSortDirection,
  } = useSortableTable(whInvoices, { key: 'report_month', direction: 'asc' }, (item, key) => {
    if (key === 'report_month') return item.report_month ?? ''
    if (key === 'invoice_number') return item.invoice_number
    if (key === 'invoice_date') return item.invoice_date ?? ''
    if (key === 'storage_fee_total') return item.storage_fee_total ?? null
    if (key === 'admin_fee_total') return item.admin_fee_total ?? null
    if (key === 'pick_fee_total') return item.pick_fee_total
    if (key === 'receiving_fee_total') return item.receiving_fee_total
    if (key === 'freight') return item.freight
    if (key === 'total') return item.total
    if (key === 'last_allocated_at') return item.last_allocated_at ?? ''
    return item[key as keyof WarehouseInvoiceSummary]
  })

  const {
    getWidth: getWhColumnWidth,
    startResize: startWhColumnResize,
    totalWidth: whTotalWidth,
  } = useColumnWidths({
    pageKey: 'warehouse-uploads-wh',
    columnKeys: [...WH_TABLE_COLUMN_KEYS],
    defaults: WH_TABLE_DEFAULT_WIDTHS,
  })

  const whTableStyle = useMemo(
    (): CSSProperties => ({
      width: whTotalWidth,
      minWidth: whTotalWidth,
      tableLayout: 'fixed',
    }),
    [whTotalWidth]
  )

  const whCellStyle = useCallback(
    (columnKey: string): CSSProperties => whFixedColumnStyle(getWhColumnWidth(columnKey)),
    [getWhColumnWidth]
  )

  useEffect(() => {
    async function loadVendors() {
      setVendorsLoading(true)
      try {
        const rows = await listWarehouseVendors()
        setVendors(rows)
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to load vendors'))
      } finally {
        setVendorsLoading(false)
      }
    }
    void loadVendors()
  }, [])

  const loadBillingLedgerCandidates = useCallback(async (reportMonths: string[]) => {
    if (reportMonths.length === 0) {
      setBillingLedgerCandidates([])
      return
    }
    const bounds = billingLedgerCandidateDateBounds(reportMonths)
    if (!bounds) {
      setBillingLedgerCandidates([])
      return
    }
    const result = await listWarehouseLedger({
      date_from: bounds.from,
      date_to: bounds.to,
      kind: 'store_draw',
      limit: 500,
    })
    setBillingLedgerCandidates(result.items)
  }, [])

  const loadProductLedgerCandidates = useCallback(async () => {
    const result = await listWarehouseLedger({
      limit: 2000,
    })
    setProductLedgerCandidates(
      result.items.filter((item) => LINKABLE_PRODUCT_LEDGER_KINDS.has(item.kind))
    )
  }, [])

  const linkedTxnsById = useMemo(() => {
    const map = new Map<string, WarehouseLedgerTransactionItem>()
    for (const txn of productLedgerCandidates) {
      map.set(txn.bk_txn_id, txn)
    }
    return map
  }, [productLedgerCandidates])

  const linkedTxnCostsById = useMemo(() => {
    const map = new Map<string, number | null>()
    for (const txn of productLedgerCandidates) {
      map.set(txn.bk_txn_id, txn.unit_cost)
    }
    return map
  }, [productLedgerCandidates])

  useEffect(() => {
    void loadBillingLedgerCandidates(activeBillingMonths)
  }, [activeBillingMonths, loadBillingLedgerCandidates, billingLines])

  useEffect(() => {
    if (!expandedProductInvoiceId) {
      setProductLedgerCandidates([])
      return
    }
    void loadProductLedgerCandidates()
  }, [expandedProductInvoiceId, loadProductLedgerCandidates, productLines])

  function bumpProductLinesFetchGen(): number {
    productLinesFetchGen.current += 1
    return productLinesFetchGen.current
  }

  // Quietly refresh expanded lines when invoice summaries update (e.g. catalog UPC save).
  useEffect(() => {
    if (!expandedProductInvoiceId) return
    const fetchGen = bumpProductLinesFetchGen()
    void listWarehouseProductInvoiceLines(expandedProductInvoiceId)
      .then((lines) => {
        if (productLinesFetchGen.current === fetchGen) setProductLines(lines)
      })
      .catch(() => {
        /* ignore — user can collapse/re-expand */
      })
  }, [invoices, expandedProductInvoiceId])

  async function loadProductLines(invoiceId: number) {
    const fetchGen = bumpProductLinesFetchGen()
    setProductLinesLoading(true)
    try {
      const lines = await listWarehouseProductInvoiceLines(invoiceId)
      if (productLinesFetchGen.current === fetchGen) setProductLines(lines)
    } catch (err) {
      toast.error(formatApiError(err))
      if (productLinesFetchGen.current === fetchGen) setProductLines([])
    } finally {
      setProductLinesLoading(false)
    }
  }

  useEffect(() => {
    if (!focusProductInvoiceLineRequest) return
    const lineId = focusProductInvoiceLineRequest.id
    const invoice = invoices.find((inv) => inv.lines.some((line) => line.id === lineId))
    if (!invoice) {
      onFocusProductInvoiceHandled?.()
      return
    }
    if (expandedProductInvoiceId !== invoice.id) {
      setExpandedProductInvoiceId(invoice.id)
      void loadProductLines(invoice.id)
    }
    const scrollTarget = () => {
      productInvoiceRowRefs.current[invoice.id]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(scrollTarget)
    })
    onFocusProductInvoiceHandled?.()
  }, [focusProductInvoiceLineRequest?.nonce, focusProductInvoiceLineRequest?.id, invoices])

  async function toggleProductInvoice(invoiceId: number) {
    if (expandedProductInvoiceId === invoiceId) {
      setExpandedProductInvoiceId(null)
      setProductLines([])
      return
    }
    setExpandedProductInvoiceId(invoiceId)
    await loadProductLines(invoiceId)
  }

  async function runAllocateSharedCostsForInvoice(invoice: WarehouseInvoiceSummary) {
    if (!invoice.report_month) {
      toast.warning('Set a report month on this invoice before allocating.')
      return
    }

    setAllocatingInvoiceId(invoice.id)
    setBusy(true)
    try {
      await allocateWarehouseWhInvoiceShipping(invoice.id)
      toast.success(
        `Allocated shared costs for ${invoice.invoice_number || `invoice ${invoice.id}`}`
      )
      await onRefresh()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setAllocatingInvoiceId(null)
      setBusy(false)
    }
  }

  async function handleWhUpload() {
    if (!whUploadMonth || !whUploadFile) return
    setBusy(true)
    try {
      const result = await uploadWarehouseWhInvoice(whUploadMonth, whUploadFile)
      toast.success(result.message)
      resetWhUploadForm()
      closeAddEditFilesModal()
      await onRefresh()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleBillingUpload() {
    if (!billingMonth || !billingFile) return
    setBusy(true)
    try {
      const result = await uploadWarehouseBilling(billingFile, {
        legacy: legacyUpload,
        reportMonth: billingMonth,
      })
      if (result.warnings.length > 0) {
        toast.warning(result.warnings.join(' '))
      }
      toast.success(result.message)
      resetBillingUploadForm()
      closeAddEditFilesModal()
      onSelectedMonthsChange([billingMonth])
      await onRefresh()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleProductUpload(files: File[], vendorId: number) {
    setBusy(true)
    try {
      let lastMessage = ''
      const warnings: string[] = []
      for (const file of files) {
        const result = await uploadWarehouseInvoice(file, vendorId)
        lastMessage = result.message
        warnings.push(...result.warnings)
      }
      if (warnings.length > 0) toast.warning(warnings.join(' '))
      toast.success(
        files.length === 1 ? lastMessage : `Uploaded ${files.length} product invoice PDFs`
      )
      resetProductUploadForm()
      closeAddEditFilesModal()
      await onRefresh()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setBusy(false)
    }
  }

  function resetWhUploadForm() {
    setWhUploadFile(null)
    setWhUploadYear('')
    setWhUploadMonthPart('')
    if (whInputRef.current) whInputRef.current.value = ''
  }

  function resetBillingUploadForm() {
    setBillingFile(null)
    setBillingYear('')
    setBillingMonthPart('')
    setLegacyUpload(false)
  }

  function resetProductUploadForm() {
    setProductUploadFiles([])
    setProductUploadVendorId('')
    if (productInputRef.current) productInputRef.current.value = ''
  }

  function closeAddEditFilesModal() {
    setAddEditFilesOpen(false)
    setAddEditFilesView('chooser')
    resetWhUploadForm()
    resetBillingUploadForm()
    resetProductUploadForm()
    setProductUploadDragging(false)
  }

  function openAddEditFilesModal() {
    setAddEditFilesView('chooser')
    setAddEditFilesOpen(true)
  }

  async function handleInvoiceVendorChange(invoiceId: number, vendorId: number) {
    try {
      await onInvoiceFieldCommit(invoiceId, { vendor_id: vendorId })
    } catch {
      // onInvoiceFieldCommit already toasts
    }
  }

  async function handleProductLineIgnore(
    line: WarehouseProductInvoiceLineItem,
    ignored: boolean,
    ignoreReason?: string | null
  ) {
    try {
      const updated = await ignoreWarehouseProductLine(line.id, ignored, ignoreReason)
      setProductLines((prev) => prev.map((row) => (row.id === updated.id ? updated : row)))
      setProductLineLinkOverlay((prev) => ({
        ...prev,
        [updated.id]: productLineOverlayPatch(updated),
      }))
      void onRefresh()
    } catch (err) {
      toast.error(formatApiError(err))
    }
  }

  async function handleProductLineUnlinkTxn(
    line: WarehouseProductInvoiceLineItem,
    bkTxnId: string
  ) {
    try {
      const updated = await unlinkWarehouseProductLineTxn(line.id, bkTxnId)
      setProductLines((prev) => prev.map((row) => (row.id === updated.id ? updated : row)))
      setProductLineLinkOverlay((prev) => ({
        ...prev,
        [updated.id]: productLineOverlayPatch(updated),
      }))
      void loadProductLedgerCandidates()
      void onRefresh()
    } catch (err) {
      toast.error(formatApiError(err))
    }
  }

  async function handleProductLineSku(
    line: WarehouseProductInvoiceLineItem,
    skuId: string
  ) {
    const previous = productLines.find((row) => row.id === line.id)
    if (!previous) return

    try {
      const updated = await setWarehouseProductInvoiceLineSku(line.id, skuId)
      setProductLines((prev) => prev.map((row) => (row.id === updated.id ? updated : row)))
    } catch (err) {
      toast.error(formatApiError(err))
    }
  }

  async function handleProductLineQtyBasis(
    line: WarehouseProductInvoiceLineItem,
    qtyBasis: 'units' | 'cases' | 'auto'
  ) {
    const previous = productLines.find((row) => row.id === line.id)
    if (!previous) return

    setProductLines((prev) =>
      prev.map((row) =>
        row.id === line.id
          ? {
              ...row,
              qty_basis: qtyBasis === 'auto' ? null : qtyBasis,
              resolved_qty_basis: qtyBasis === 'auto' ? row.resolved_qty_basis : qtyBasis,
            }
          : row
      )
    )

    try {
      const updated = await setWarehouseProductLineQtyBasis(line.id, qtyBasis)
      setProductLines((prev) => prev.map((row) => (row.id === updated.id ? updated : row)))
    } catch (err) {
      setProductLines((prev) =>
        prev.map((row) => (row.id === line.id ? previous : row))
      )
      toast.error(formatApiError(err))
    }
  }

  async function handleProductLinePatch(
    line: WarehouseProductInvoiceLineItem,
    payload: WarehouseProductInvoiceLinePatchRequest,
    optimistic: Partial<WarehouseProductInvoiceLineItem>
  ) {
    const previous = productLines.find((row) => row.id === line.id)
    if (!previous) return

    setProductLines((prev) =>
      prev.map((row) => (row.id === line.id ? { ...row, ...optimistic } : row))
    )

    try {
      const updated = await patchWarehouseProductInvoiceLine(line.id, payload)
      setProductLines((prev) => prev.map((row) => (row.id === updated.id ? updated : row)))
      bumpProductLinesFetchGen()
      void onRefresh()
    } catch (err) {
      setProductLines((prev) =>
        prev.map((row) => (row.id === line.id ? previous : row))
      )
      toast.error(formatApiError(err))
    }
  }

  async function handleAddProductLine(invoiceId: number) {
    if (disabled || productLineSaving) return
    setProductLineSaving(true)
    try {
      const created = await createWarehouseProductInvoiceLine(invoiceId)
      setProductLines((prev) => [...prev, created])
      void onRefresh()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setProductLineSaving(false)
    }
  }

  async function handleDeleteProductLine(line: WarehouseProductInvoiceLineItem) {
    if (productLineLinkedTxnIds(line).length > 0) {
      toast.error('Cannot delete a line linked to a ledger transaction. Unlink it first.')
      return
    }
    const confirmed = window.confirm('Delete this product line?')
    if (!confirmed) return

    setProductLineSaving(true)
    try {
      await deleteWarehouseProductInvoiceLine(line.id)
      setProductLines((prev) => prev.filter((row) => row.id !== line.id))
      void onRefresh()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setProductLineSaving(false)
    }
  }

  async function handleProductLineLink(lineId: number, bkTxnId: string) {
    const existingLine = productLines.find((row) => row.id === lineId)
    if (existingLine && productLineLinkedTxnIds(existingLine).includes(bkTxnId)) {
      toast.warning(`Already linked to ${shortTxnId(bkTxnId)}`)
      return
    }

    const invoiceNumber =
      invoices.find((inv) => inv.id === expandedProductInvoiceId)?.invoice_number ??
      String(expandedProductInvoiceId ?? lineId)

    try {
      const result = await linkWarehouseProductLine(lineId, bkTxnId)
      const priorIds = existingLine ? productLineLinkedTxnIds(existingLine) : []
      const linkedIds = result.linked_bk_txn_ids?.length
        ? result.linked_bk_txn_ids
        : [...priorIds, result.bk_txn_id]
      const linkedTxnId = result.bk_txn_id || bkTxnId
      toast.success(`Linked ${shortTxnId(linkedTxnId)} to invoice ${invoiceNumber}`)
      setProductLines((prev) =>
        prev.map((row) => {
          if (row.id !== lineId) return row
          return {
            ...row,
            // Keep existing primary when adding another txn; only set if none yet.
            linked_bk_txn_id: row.linked_bk_txn_id ?? linkedTxnId,
            linked_bk_txn_ids: linkedIds,
            ignored: false,
          }
        })
      )
      setProductLineLinkOverlay((prev) => ({
        ...prev,
        [lineId]: {
          ignored: false,
          linked_bk_txn_id: existingLine?.linked_bk_txn_id ?? linkedTxnId,
          linked_bk_txn_ids: linkedIds,
          ignore_reason: null,
        },
      }))
      void loadProductLedgerCandidates()
      void onRefresh()
    } catch (err) {
      toast.error(formatApiError(err))
    }
  }

  async function handleBillingLineLink(lineId: number, bkTxnId: string) {
    const existingLine = billingLines.find((row) => row.id === lineId)
    const currentLinked =
      billingLineLinkOverlay[lineId]?.linked_bk_txn_id ?? existingLine?.linked_bk_txn_id
    if (currentLinked === bkTxnId) {
      toast.warning(`Already linked to ${shortTxnId(bkTxnId)}`)
      return
    }

    try {
      const result = await linkWarehouseBillingLine(lineId, bkTxnId)
      const linkedTxnId = result.bk_txn_id || bkTxnId
      toast.success(`Linked billing line to ${shortTxnId(linkedTxnId)}`)
      setBillingLineLinkOverlay((prev) => ({
        ...prev,
        [lineId]: { linked_bk_txn_id: linkedTxnId },
      }))
      setBillingLedgerCandidates((prev) =>
        prev.map((txn) =>
          txn.bk_txn_id === linkedTxnId ? { ...txn, billing_line_id: lineId } : txn
        )
      )
      void onRefresh()
    } catch (err) {
      toast.error(formatApiError(err))
    }
  }

  const awarenessByLineId = useMemo(() => {
    const map = new Map<number, NonNullable<typeof awareness>['rows'][number]>()
    for (const row of awareness?.rows ?? []) {
      map.set(row.billing_line_id, row)
    }
    return map
  }, [awareness])

  const billingLedgerByTxnId = useMemo(() => {
    const map = new Map<string, WarehouseLedgerTransactionItem>()
    for (const txn of billingLedgerCandidates) {
      map.set(txn.bk_txn_id, txn)
    }
    return map
  }, [billingLedgerCandidates])

  const disabled = loading || busy

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="w-[150px] shrink-0">
          <MultiSelectFilter
            id="uploads-month-filter"
            label="Month"
            options={monthOptions}
            selected={selectedMonths}
            onChange={onSelectedMonthsChange}
            searchable
          />
        </div>
        <div className="flex items-center gap-2">
          {syncAction}
          <button
            type="button"
            className="btn btn-secondary h-8 shrink-0 px-3 text-xs"
            disabled={disabled}
            onClick={() => openAddEditFilesModal()}
          >
            Add / Edit Files
          </button>
        </div>
      </div>

      <PanelCard
        step={1}
        title="Warehouse invoices"
        errorCount={whErrorCount}
        description="Upload monthly WH service PDFs. Review and edit fee fields to confirm details match the invoice, then allocate shared costs to ledger store draws and inbound transactions for that month."
      >
        {whInvoices.length === 0 ? (
          <p className="text-sm text-gray-500">No warehouse service invoices uploaded yet.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-100">
            <div className="table-scroll-mobile overflow-x-auto">
              <table
                className="table-forecast border-separate border-spacing-0"
                style={whTableStyle}
              >
                <thead>
                  <tr>
                    <WhInvoiceSortableHeader
                      label="Report month"
                      sortKey="report_month"
                      sortDirection={getWhSortDirection('report_month')}
                      onSort={requestWhSort}
                      columnKey="month"
                      width={getWhColumnWidth('month')}
                      onStartResize={startWhColumnResize}
                    />
                    <WhInvoiceSortableHeader
                      label="WH invoice #"
                      sortKey="invoice_number"
                      sortDirection={getWhSortDirection('invoice_number')}
                      onSort={requestWhSort}
                      columnKey="invoice_number"
                      width={getWhColumnWidth('invoice_number')}
                      onStartResize={startWhColumnResize}
                    />
                    <WhInvoiceSortableHeader
                      label="Date"
                      sortKey="invoice_date"
                      sortDirection={getWhSortDirection('invoice_date')}
                      onSort={requestWhSort}
                      columnKey="invoice_date"
                      width={getWhColumnWidth('invoice_date')}
                      onStartResize={startWhColumnResize}
                    />
                    <WhInvoiceSortableHeader
                      label={
                        <HoverHelp label="Storage" aria-label="Storage column help">
                          <p>
                            MONTHLY STORAGE line items on the Halo WH service invoice. BookKeeper uses
                            these totals in month-end journal entries (by store activity), not via
                            Allocate shared costs.
                          </p>
                        </HoverHelp>
                      }
                      sortKey="storage_fee_total"
                      sortDirection={getWhSortDirection('storage_fee_total')}
                      onSort={requestWhSort}
                      columnKey="storage_fee_total"
                      width={getWhColumnWidth('storage_fee_total')}
                      onStartResize={startWhColumnResize}
                      className="text-right"
                    />
                    <WhInvoiceSortableHeader
                      label={
                        <HoverHelp label="Admin" aria-label="Admin column help">
                          <p>
                            MONTHLY ADMIN FEE line items on the invoice. Same month-end journal entry
                            path as storage; not allocated to ledger rows.
                          </p>
                        </HoverHelp>
                      }
                      sortKey="admin_fee_total"
                      sortDirection={getWhSortDirection('admin_fee_total')}
                      onSort={requestWhSort}
                      columnKey="admin_fee_total"
                      width={getWhColumnWidth('admin_fee_total')}
                      onStartResize={startWhColumnResize}
                      className="text-right"
                    />
                    <WhInvoiceSortableHeader
                      label={
                        <HoverHelp label="Pick fees" aria-label="Pick fees column help">
                          <p>
                            CASE PICK and INDIVIDUAL PICKS service fees from the invoice. Allocate
                            shared costs spreads this total across store_draw ledger rows as
                            pick_fee_alloc.
                          </p>
                        </HoverHelp>
                      }
                      sortKey="pick_fee_total"
                      sortDirection={getWhSortDirection('pick_fee_total')}
                      onSort={requestWhSort}
                      columnKey="pick_fee_total"
                      width={getWhColumnWidth('pick_fee_total')}
                      onStartResize={startWhColumnResize}
                      className="text-right"
                    />
                    <WhInvoiceSortableHeader
                      label={
                        <HoverHelp label="Receiving" aria-label="Receiving column help">
                          <p>
                            PALLETS RECEIVED, RECEIVING INDIVIDUAL BOXES, RECEIVING BOXES IN, and
                            RECEIVING PALLET IN line items, summed from the invoice. Allocate shared
                            costs spreads this across
                            manufacturer_purchase and initial_store_transfer rows as receiving_alloc,
                            proportional by case count.
                          </p>
                        </HoverHelp>
                      }
                      sortKey="receiving_fee_total"
                      sortDirection={getWhSortDirection('receiving_fee_total')}
                      onSort={requestWhSort}
                      columnKey="receiving_fee_total"
                      width={getWhColumnWidth('receiving_fee_total')}
                      onStartResize={startWhColumnResize}
                      className="text-right"
                    />
                    <WhInvoiceSortableHeader
                      label={
                        <HoverHelp
                          label="Shipping / freight"
                          aria-label="Shipping / freight column help"
                        >
                          <p>
                            Freight and handling charged after the invoice subtotal. Allocate shared
                            costs spreads this across store_draw ledger rows as shipping_alloc.
                          </p>
                        </HoverHelp>
                      }
                      sortKey="freight"
                      sortDirection={getWhSortDirection('freight')}
                      onSort={requestWhSort}
                      columnKey="freight"
                      width={getWhColumnWidth('freight')}
                      onStartResize={startWhColumnResize}
                      className="text-right"
                    />
                    <WhInvoiceSortableHeader
                      label="Total"
                      sortKey="total"
                      sortDirection={getWhSortDirection('total')}
                      onSort={requestWhSort}
                      columnKey="total"
                      width={getWhColumnWidth('total')}
                      onStartResize={startWhColumnResize}
                      className="text-right"
                    />
                    <WhInvoiceSortableHeader
                      label="Last allocated"
                      sortKey="last_allocated_at"
                      sortDirection={getWhSortDirection('last_allocated_at')}
                      onSort={requestWhSort}
                      columnKey="last_allocated"
                      width={getWhColumnWidth('last_allocated')}
                      onStartResize={startWhColumnResize}
                    />
                    <th
                      className="relative"
                      style={{
                        ...whFixedColumnStyle(getWhColumnWidth('allocate')),
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      Allocate
                      <ColumnResizeHandle
                        columnKey="allocate"
                        onStartResize={startWhColumnResize}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedWhInvoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="font-medium text-gray-900" style={whCellStyle('month')}>
                        {invoice.report_month ?? '—'}
                      </td>
                      <td style={whCellStyle('invoice_number')}>
                        <EditableTextCell
                          value={invoice.invoice_number}
                          disabled={disabled}
                          onCommit={(next) =>
                            onInvoiceFieldCommit(invoice.id, { invoice_number: next })
                          }
                        />
                      </td>
                      <td style={whCellStyle('invoice_date')}>
                        <EditableDateCell
                          value={invoice.invoice_date}
                          disabled={disabled}
                          onCommit={(next) =>
                            onInvoiceFieldCommit(invoice.id, { invoice_date: next })
                          }
                        />
                      </td>
                      <td className="text-right" style={whCellStyle('storage_fee_total')}>
                        <EditableNullableMoneyCell
                          value={invoice.storage_fee_total}
                          disabled={disabled}
                          onCommit={(next) => {
                            if (next == null) return Promise.resolve()
                            return onInvoiceFieldCommit(invoice.id, { storage_fee_total: next })
                          }}
                        />
                      </td>
                      <td className="text-right" style={whCellStyle('admin_fee_total')}>
                        <EditableNullableMoneyCell
                          value={invoice.admin_fee_total}
                          disabled={disabled}
                          onCommit={(next) => {
                            if (next == null) return Promise.resolve()
                            return onInvoiceFieldCommit(invoice.id, { admin_fee_total: next })
                          }}
                        />
                      </td>
                      <td className="text-right" style={whCellStyle('pick_fee_total')}>
                        <EditableMoneyCell
                          value={invoice.pick_fee_total}
                          disabled={disabled}
                          onCommit={(next) =>
                            onInvoiceFieldCommit(invoice.id, { pick_fee_total: next })
                          }
                        />
                      </td>
                      <td className="text-right" style={whCellStyle('receiving_fee_total')}>
                        <EditableMoneyCell
                          value={invoice.receiving_fee_total}
                          disabled={disabled}
                          onCommit={(next) =>
                            onInvoiceFieldCommit(invoice.id, { receiving_fee_total: next })
                          }
                        />
                      </td>
                      <td className="text-right" style={whCellStyle('freight')}>
                        <EditableMoneyCell
                          value={invoice.freight}
                          disabled={disabled}
                          onCommit={(next) =>
                            onInvoiceFieldCommit(invoice.id, { freight: next })
                          }
                        />
                      </td>
                      <td className="text-right" style={whCellStyle('total')}>
                        <EditableMoneyCell
                          value={invoice.total}
                          disabled={disabled}
                          onCommit={(next) => onInvoiceFieldCommit(invoice.id, { total: next })}
                        />
                      </td>
                      <td className="text-sm text-gray-600" style={whCellStyle('last_allocated')}>
                        {formatLastAllocated(invoice.last_allocated_at)}
                      </td>
                      <td style={whCellStyle('allocate')}>
                        <button
                          type="button"
                          className="btn btn-primary h-8 px-2.5 text-xs"
                          disabled={
                            disabled ||
                            !invoice.report_month ||
                            allocatingInvoiceId === invoice.id
                          }
                          title="Distributes this invoice's freight and pick fees across store draws, and receiving fees across inbound ledger rows for the month (overwrites shipping_alloc, pick_fee_alloc, and receiving_alloc)."
                          onClick={() => void runAllocateSharedCostsForInvoice(invoice)}
                        >
                          {allocatingInvoiceId === invoice.id ? 'Allocating…' : 'Allocate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </PanelCard>

      <PanelCard
        step={2}
        title="Product invoices"
        errorCount={productErrorCount}
        description="Upload vendor product invoice PDFs. Expand an invoice to add or delete lines, edit units/cases/pricing, and allocate manually — line totals must match the extracted invoice. Set vendor, link ledger txns, or ignore store-direct lines. SKU fills from vendor annotations when present, otherwise from catalog name match when possible."
      >
        {productInvoices.length === 0 ? (
          <p className="text-sm text-gray-500">No product invoices uploaded yet.</p>
        ) : (
          <div className="space-y-2">
            {productInvoices.map((invoice) => {
              const expanded = expandedProductInvoiceId === invoice.id
              const linesForStatus =
                expanded && productLines.length > 0
                  ? productLines.map((line) => ({
                      ...line,
                      ...(productLineLinkOverlay[line.id] ?? {}),
                    }))
                  : invoice.lines.map((line) => ({
                      ...line,
                      ...(line.id != null ? productLineLinkOverlay[line.id] ?? {} : {}),
                    }))
              const linkBadges = productInvoiceLinkBadges({
                ...invoice,
                lines: linesForStatus,
              })
              const showProductUploadErrors = uploadsErrorVisibleForMonth(
                productInvoiceErrorMonth(invoice)
              )
              return (
                <div
                  key={invoice.id}
                  ref={(el) => {
                    productInvoiceRowRefs.current[invoice.id] = el
                  }}
                  className="rounded-lg border border-gray-200"
                >
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
                    onClick={() => void toggleProductInvoice(invoice.id)}
                  >
                    {expanded ? (
                      <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-500" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-500" />
                    )}
                    <span className="font-medium text-gray-900">{invoice.invoice_number}</span>
                    <span className="text-xs text-gray-500">{invoice.invoice_date ?? '—'}</span>
                    <span className="text-xs text-gray-500">{invoice.vendor_name ?? '—'}</span>
                    <span className="text-xs text-gray-500">{formatMoney(invoice.total)}</span>
                    {showProductUploadErrors &&
                    (linkBadges.length > 0 || invoice.allocation_valid === false) ? (
                      <span className="ml-auto flex items-center gap-1.5">
                        {invoice.allocation_valid === false ? (
                          <span className="inline-flex rounded-md bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-800 ring-1 ring-red-200">
                            Allocation mismatch
                          </span>
                        ) : null}
                        {linkBadges.map((badge) => (
                          <span
                            key={badge.label}
                            className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ${badge.className}`}
                          >
                            {badge.label}
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </button>
                  {expanded ? (
                    <div className="border-t border-gray-100 px-3 py-3">
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-medium text-gray-700">Vendor</span>
                          <InvoiceVendorSelect
                            options={vendorSelectOptions}
                            value={invoice.vendor_id ?? null}
                            disabled={disabled || vendorsLoading}
                            onSelect={(vendorId) => handleInvoiceVendorChange(invoice.id, vendorId)}
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn-secondary h-7 px-2.5 text-xs"
                          disabled={disabled || productLineSaving}
                          onClick={() => void handleAddProductLine(invoice.id)}
                        >
                          Add line
                        </button>
                      </div>
                      <ProductInvoiceAllocationBanner
                        invoice={invoice}
                        productLines={productLines}
                      />
                      {productLinesLoading ? (
                        <p className="text-sm text-gray-500">
                          <ArrowPathIcon className="mr-1 inline h-4 w-4 animate-spin" />
                          Loading lines…
                        </p>
                      ) : productLines.length === 0 ? (
                        <p className="text-sm text-gray-500">No product lines on this invoice.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="table-forecast border-separate border-spacing-0 min-w-max text-sm">
                            <thead>
                              <tr>
                                <th>Product</th>
                                <th>Description</th>
                                <th>Units</th>
                                <th>Cases</th>
                                <th>$/case</th>
                                <th>Extended</th>
                                <th>
                                  <HoverHelp label="Landed $/case" aria-label="Landed cost per case help">
                                    <p>
                                      Extended plus this line&apos;s share of the invoice&apos;s
                                      freight, setup and drop-ship fees, and any tax billed below
                                      the subtotal, divided by cases. This is the cost the ledger
                                      uses for linked receipts. Read-only.
                                    </p>
                                  </HoverHelp>
                                </th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {productLines.map((line) => {
                                const lineLinked = productLineLinkedTxnIds(line).length > 0
                                const deleteDisabled = disabled || productLineSaving || lineLinked
                                return (
                                <tr key={line.id} className="[&>td]:align-middle [&>td]:!px-2 [&>td]:!py-1.5">
                                  <td>
                                    <div className="flex flex-col items-start gap-0.5">
                                      <ProductLineSkuSelect
                                        options={catalogSkuSelectOptions}
                                        value={line.sku_id}
                                        disabled={disabled}
                                        onSelect={(skuId) => handleProductLineSku(line, skuId)}
                                      />
                                      {line.sku_id && onOpenCatalogSku ? (
                                        <button
                                          type="button"
                                          className="text-[11px] text-blue-600 hover:text-blue-800 hover:underline"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            onOpenCatalogSku(line.sku_id!)
                                          }}
                                        >
                                          Open in Catalog
                                        </button>
                                      ) : null}
                                    </div>
                                  </td>
                                  <td>
                                    <div className={READONLY_CELL}>
                                      {line.product_name ?? line.description}
                                    </div>
                                  </td>
                                  <td className="tabular-nums">
                                    <EditableNullableQuantityCell
                                      value={line.unit_count}
                                      disabled={disabled}
                                      onCommit={(next) =>
                                        handleProductLinePatch(
                                          line,
                                          { unit_count: next },
                                          { unit_count: next }
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="tabular-nums">
                                    <div className="flex flex-col items-start gap-0.5">
                                      <EditableNullableQuantityCell
                                        value={line.case_count}
                                        disabled={disabled}
                                        onCommit={(next) =>
                                          handleProductLinePatch(
                                            line,
                                            { case_count: next },
                                            productLineMoneyOptimistic(line, { case_count: next })
                                          )
                                        }
                                      />
                                      <ProductLineQtyBasisControl
                                        line={line}
                                        disabled={disabled}
                                        onBasisChange={(qtyBasis) =>
                                          handleProductLineQtyBasis(line, qtyBasis)
                                        }
                                      />
                                    </div>
                                  </td>
                                  <td className="tabular-nums">
                                    <EditableNullableMoneyCell
                                      value={line.price_per_case}
                                      disabled={disabled}
                                      allowClear
                                      onCommit={(next) =>
                                        handleProductLinePatch(
                                          line,
                                          { price_per_case: next },
                                          productLineMoneyOptimistic(line, { price_per_case: next })
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="tabular-nums">
                                    <div className={READONLY_CELL}>
                                      <span
                                        className={
                                          line.price_per_case == null ? 'text-gray-300' : undefined
                                        }
                                      >
                                        {formatProductLineExtended(line)}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="tabular-nums">
                                    <div
                                      className={READONLY_CELL}
                                      title={
                                        line.charge_alloc != null
                                          ? `Landed total ${formatMoney(line.landed_extended)} (includes ${formatMoney(line.charge_alloc)} of invoice charges)`
                                          : undefined
                                      }
                                    >
                                      {formatLandedCost(line.landed_price_per_case)}
                                    </div>
                                  </td>
                                  <td className="max-w-[350px]">
                                    <div className={`${READONLY_CELL} max-w-[350px]`}>
                                    <div className="flex max-w-[350px] flex-col items-start gap-1">
                                      {line.ignored ? (
                                        <span className="inline-flex items-center gap-1">
                                          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                                            Ignored
                                          </span>
                                          {line.ignore_reason ? (
                                            <HoverHelp
                                              label="Reason"
                                              aria-label="Ignore reason"
                                              className="text-xs text-gray-600"
                                            >
                                              <p className="whitespace-pre-wrap">
                                                {line.ignore_reason}
                                              </p>
                                            </HoverHelp>
                                          ) : null}
                                        </span>
                                      ) : lineLinked ? (
                                        <>
                                          <ProductLineLinkedTxnChips
                                            txnIds={productLineLinkedTxnIds(line)}
                                            disabled={disabled}
                                            linkedTxnsById={linkedTxnsById}
                                            onUnlinkTxn={(bkTxnId) =>
                                              void handleProductLineUnlinkTxn(line, bkTxnId)
                                            }
                                            onJumpToLedgerTxn={onJumpToLedgerTxn}
                                          />
                                          <ProductLineMismatchHints
                                            line={line}
                                            linkedTxnCostsById={linkedTxnCostsById}
                                          />
                                        </>
                                      ) : (
                                        <span className="rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                                          Needs link
                                        </span>
                                      )}
                                    </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="flex flex-col items-start gap-1.5">
                                      {line.ignored ? (
                                        <ProductLineMatchControls
                                          line={line}
                                          candidates={productLedgerCandidates}
                                          disabled={disabled}
                                          onIgnore={(ignored, ignoreReason) =>
                                            handleProductLineIgnore(line, ignored, ignoreReason)
                                          }
                                          onLink={(bkTxnId) =>
                                            handleProductLineLink(line.id, bkTxnId)
                                          }
                                        />
                                      ) : lineLinked ? (
                                        <ProductLineLinkPicker
                                          lineId={line.id}
                                          candidates={productLedgerCandidates}
                                          disabled={disabled}
                                          onLink={(bkTxnId) =>
                                            handleProductLineLink(line.id, bkTxnId)
                                          }
                                        />
                                      ) : (
                                        <ProductLineMatchControls
                                          line={line}
                                          candidates={productLedgerCandidates}
                                          disabled={disabled}
                                          onIgnore={(ignored, ignoreReason) =>
                                            handleProductLineIgnore(line, ignored, ignoreReason)
                                          }
                                          onLink={(bkTxnId) =>
                                            handleProductLineLink(line.id, bkTxnId)
                                          }
                                        />
                                      )}
                                      <button
                                        type="button"
                                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:text-gray-300"
                                        disabled={deleteDisabled}
                                        aria-label={
                                          lineLinked
                                            ? 'Unlink all ledger transactions before deleting'
                                            : 'Delete this product line'
                                        }
                                        title={
                                          lineLinked
                                            ? 'Unlink all ledger transactions before deleting'
                                            : 'Delete this product line'
                                        }
                                        onClick={() => void handleDeleteProductLine(line)}
                                      >
                                        <TrashIcon className="h-3.5 w-3.5" aria-hidden />
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )})}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        )}
      </PanelCard>

      <PanelCard
        step={3}
        title="Monthly billing detail"
        errorCount={billingErrorCount}
        description="Upload TXCGB billing spreadsheets and link each line to a store_draw ledger transaction. Qty check compares billing cases to ledger cases — mismatches are shown for review only and never auto-edit the ledger."
      >
        {billingLines.length === 0 ? (
          <p className="text-sm text-gray-500">No billing files uploaded yet.</p>
        ) : billingLinesFiltered.length === 0 ? (
          <p className="text-sm text-gray-500">No billing lines for the selected month(s).</p>
        ) : (
          <div className="space-y-3">
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="table-forecast border-separate border-spacing-0 min-w-max text-sm">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>SKU</th>
                    <th>Location</th>
                    <th>Qty</th>
                    <th>Pick fee</th>
                    <th>Shipping</th>
                    <th>
                      <HoverHelp label="Qty check" aria-label="Qty check column help">
                        <p>
                          Compares this billing line&apos;s case qty to the linked (or best-match)
                          ledger <code>store_draw</code> cases. Read-only — never changes ledger
                          quantities.
                        </p>
                      </HoverHelp>
                    </th>
                    <th>
                      <HoverHelp label="Linked txn" aria-label="Linked txn column help">
                        <p>
                          Green tag shows the short BK Txn ID for the linked ledger row. Click to
                          open Inventory → Ledger filtered to that transaction.
                        </p>
                      </HoverHelp>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {billingLinesDisplay.map((line) => {
                    const aware = awarenessByLineId.get(line.id)
                    const linkedTxn = line.linked_bk_txn_id
                      ? billingLedgerByTxnId.get(line.linked_bk_txn_id)
                      : undefined

                    const billingQtyErrorVisible =
                      Boolean(aware?.qty_mismatch) &&
                      uploadsErrorVisibleForMonth(line.report_month)

                    return (
                      <tr
                        key={line.id}
                        className={billingQtyErrorVisible ? 'bg-red-50/60' : undefined}
                      >
                        <td>{line.order_id}</td>
                        <td>{line.bulk_sku ?? line.item_sku}</td>
                        <td className="text-xs text-gray-700">{line.location ?? '—'}</td>
                        <td className="tabular-nums">{line.item_qty}</td>
                        <td className="tabular-nums">{formatMoney(line.pick_fee)}</td>
                        <td className="tabular-nums">{formatMoney(line.shipping_total)}</td>
                        <td className="text-xs">{billingQtyCheckLabel(aware, line.report_month)}</td>
                        <td>
                          {line.linked_bk_txn_id ? (
                            <BillingLinkedTxnChip
                              bkTxnId={line.linked_bk_txn_id}
                              txn={linkedTxn}
                              disabled={disabled}
                              onJumpToLedgerTxn={onJumpToLedgerTxn}
                            />
                          ) : (
                            <BillingMatchPicker
                              candidates={billingLedgerCandidates}
                              disabled={disabled}
                              onLink={(bkTxnId) => handleBillingLineLink(line.id, bkTxnId)}
                            />
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </PanelCard>

      {addEditFilesOpen ? (
        <UploadModal
          title={
            addEditFilesView === 'chooser'
              ? 'Add / Edit Files'
              : addEditFilesView === 'wh'
                ? 'Upload warehouse invoice (PDF)'
                : addEditFilesView === 'product'
                  ? 'Upload product invoices (PDFs)'
                  : addEditFilesView === 'billing'
                    ? 'Upload monthly billing detail'
                    : 'Previously uploaded files'
          }
          onClose={() => {
            if (disabled) return
            closeAddEditFilesModal()
          }}
          onBack={
            addEditFilesView !== 'chooser'
              ? () => setAddEditFilesView('chooser')
              : undefined
          }
          submitLabel={
            addEditFilesView === 'product' && disabled
              ? 'Uploading…'
              : 'Upload'
          }
          showSubmit={
            addEditFilesView === 'wh' ||
            addEditFilesView === 'product' ||
            addEditFilesView === 'billing'
          }
          submitDisabled={
            addEditFilesView === 'wh'
              ? !whUploadMonth || !whUploadFile || disabled
              : addEditFilesView === 'product'
                ? disabled ||
                  !productUploadVendorId ||
                  productUploadFiles.length === 0 ||
                  vendorSelectOptions.length === 0 ||
                  !productUploadVendorHasParser
                : addEditFilesView === 'billing'
                  ? !billingMonth || !billingFile || disabled
                  : true
          }
          onSubmit={() => {
            if (addEditFilesView === 'wh') void handleWhUpload()
            else if (addEditFilesView === 'billing') void handleBillingUpload()
            else if (addEditFilesView === 'product') {
              const vendorId = Number(productUploadVendorId)
              if (!Number.isFinite(vendorId) || productUploadFiles.length === 0) return
              if (!productUploadVendorHasParser) return
              void handleProductUpload(productUploadFiles, vendorId)
            }
          }}
          wide={addEditFilesView === 'manage'}
        >
          {addEditFilesView === 'chooser' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <button
                  type="button"
                  className="flex min-h-[8.5rem] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white p-4 text-center transition-colors hover:border-brand-500 hover:ring-2 hover:ring-brand-500/20"
                  disabled={disabled}
                  onClick={() => setAddEditFilesView('wh')}
                >
                  <CubeIcon className="h-8 w-8 text-gray-500" aria-hidden="true" />
                  <span className="text-sm font-medium text-gray-900">Warehouse invoices</span>
                </button>
                <button
                  type="button"
                  className="flex min-h-[8.5rem] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white p-4 text-center transition-colors hover:border-brand-500 hover:ring-2 hover:ring-brand-500/20"
                  disabled={disabled || vendorsLoading}
                  onClick={() => setAddEditFilesView('product')}
                >
                  <DocumentTextIcon className="h-8 w-8 text-gray-500" aria-hidden="true" />
                  <span className="text-sm font-medium text-gray-900">Product invoices</span>
                </button>
                <button
                  type="button"
                  className="flex min-h-[8.5rem] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white p-4 text-center transition-colors hover:border-brand-500 hover:ring-2 hover:ring-brand-500/20"
                  disabled={disabled}
                  onClick={() => setAddEditFilesView('billing')}
                >
                  <TableCellsIcon className="h-8 w-8 text-gray-500" aria-hidden="true" />
                  <span className="text-sm font-medium text-gray-900">Monthly billing</span>
                </button>
              </div>
              <div className="text-center">
                <button
                  type="button"
                  className="text-sm font-medium text-brand-700 hover:text-brand-800"
                  onClick={() => setAddEditFilesView('manage')}
                >
                  View previously uploaded files
                </button>
              </div>
            </div>
          ) : null}

          {addEditFilesView === 'wh' ? (
            <>
              <MonthPickers
                year={whUploadYear}
                monthPart={whUploadMonthPart}
                disabled={disabled}
                onYearChange={setWhUploadYear}
                onMonthPartChange={setWhUploadMonthPart}
              />
              <FilePicker
                label="Select PDF file"
                accept=".pdf,application/pdf"
                file={whUploadFile}
                inputRef={whInputRef}
                disabled={disabled}
                onFileChange={setWhUploadFile}
              />
            </>
          ) : null}

          {addEditFilesView === 'product' ? (
            <>
              <div className="space-y-1">
                <span className="text-xs font-medium text-gray-600">Vendor</span>
                {vendorSelectOptions.length === 0 ? (
                  <p className="text-xs text-amber-700">
                    No vendors are available yet. Add vendors on the Vendors page before uploading
                    PDFs.
                  </p>
                ) : (
                  <>
                    <SearchableSelect
                      options={vendorSelectOptions}
                      value={productUploadVendorId}
                      onChange={(id) => setProductUploadVendorId(id ?? '')}
                      disabled={disabled}
                      size="compact"
                      preserveOrder
                      preferBelow
                      useViewportForPlacement
                      className="w-full"
                      placeholder="Select vendor…"
                    />
                    <p className="text-xs text-gray-500">
                      Select the vendor for the PDFs you&apos;re uploading; upload requires a vendor
                      that already has a parser.
                    </p>
                    {productUploadSelectedVendor &&
                    !productUploadSelectedVendor.has_invoice_parser ? (
                      <p className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
                        <ExclamationTriangleIcon
                          className="mt-0.5 h-4 w-4 shrink-0"
                          aria-hidden="true"
                        />
                        <span>
                          A custom invoice parser is not built yet for{' '}
                          <span className="font-medium">{productUploadSelectedVendor.name}</span>.
                          Choose a vendor with a parser to upload PDFs.
                        </span>
                      </p>
                    ) : null}
                  </>
                )}
              </div>
              <ProductInvoiceFileDropzone
                files={productUploadFiles}
                disabled={disabled}
                dragging={productUploadDragging}
                inputRef={productInputRef}
                onDraggingChange={setProductUploadDragging}
                onFilesChange={setProductUploadFiles}
              />
            </>
          ) : null}

          {addEditFilesView === 'billing' ? (
            <>
              <MonthPickers
                year={billingYear}
                monthPart={billingMonthPart}
                disabled={disabled}
                onYearChange={setBillingYear}
                onMonthPartChange={setBillingMonthPart}
              />
              {billingMonth && billingMonth < LEGACY_PACKAGE_CUTOFF ? (
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={legacyUpload}
                    disabled={disabled}
                    onChange={(e) => setLegacyUpload(e.target.checked)}
                  />
                  Legacy backfill format (pre-2026-06)
                </label>
              ) : null}
              <FilePicker
                label="Select XLSX file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                file={billingFile}
                disabled={disabled}
                onFileChange={setBillingFile}
              />
            </>
          ) : null}

          {addEditFilesView === 'manage' ? (
            <ManageUploadedFilesView
              disabled={disabled}
              whInvoices={manageWhInvoices}
              productInvoices={manageProductInvoices}
              billingMonths={manageBillingMonths}
              onDeleteInvoice={onDeleteInvoice}
              onDeleteBillingMonth={onDeleteBillingMonth}
            />
          ) : null}
        </UploadModal>
      ) : null}
    </div>
  )
}

function MonthPickers({
  year,
  monthPart,
  disabled,
  onYearChange,
  onMonthPartChange,
}: {
  year: string
  monthPart: string
  disabled?: boolean
  onYearChange: (value: string) => void
  onMonthPartChange: (value: string) => void
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <select
        className="input-sm select"
        value={monthPart}
        disabled={disabled}
        onChange={(e) => onMonthPartChange(e.target.value)}
      >
        <option value="">Month</option>
        {PACKAGE_MONTH_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <select
        className="input-sm select"
        value={year}
        disabled={disabled}
        onChange={(e) => onYearChange(e.target.value)}
      >
        <option value="">Year</option>
        {packageYearOptions().map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function ProductInvoiceFileDropzone({
  files,
  disabled,
  dragging,
  inputRef,
  onDraggingChange,
  onFilesChange,
}: {
  files: File[]
  disabled?: boolean
  dragging: boolean
  inputRef: RefObject<HTMLInputElement | null>
  onDraggingChange: (dragging: boolean) => void
  onFilesChange: (files: File[]) => void
}) {
  const dragCounterRef = useRef(0)

  function appendPdfFiles(fileList: FileList | File[] | null | undefined) {
    if (!fileList || fileList.length === 0) return
    const incoming = Array.from(fileList).filter(
      (file) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    )
    if (incoming.length === 0) {
      toast.warning('Only PDF files can be uploaded.')
      return
    }
    onFilesChange([...files, ...incoming])
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-gray-600">Select PDF files</span>
      <div
        className={`rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
          dragging
            ? 'border-brand-500 bg-brand-50'
            : 'border-gray-200 bg-gray-50 hover:border-gray-300'
        }`}
        onDragEnter={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (disabled) return
          dragCounterRef.current += 1
          onDraggingChange(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (disabled) return
          dragCounterRef.current -= 1
          if (dragCounterRef.current <= 0) {
            dragCounterRef.current = 0
            onDraggingChange(false)
          }
        }}
        onDragOver={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        onDrop={(e) => {
          e.preventDefault()
          e.stopPropagation()
          dragCounterRef.current = 0
          onDraggingChange(false)
          if (disabled) return
          appendPdfFiles(e.dataTransfer.files)
        }}
      >
        <CloudArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" />
        <p className="mt-2 text-sm text-gray-700">
          Drag and drop PDFs here, or{' '}
          <label className="cursor-pointer font-medium text-brand-700 hover:text-brand-800">
            browse
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              className="sr-only"
              disabled={disabled}
              onChange={(e) => {
                appendPdfFiles(e.target.files)
                if (inputRef.current) inputRef.current.value = ''
              }}
            />
          </label>
        </p>
        <p className="mt-1 text-xs text-gray-500">One or more product invoice PDFs</p>
      </div>
      {files.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${index}`}
              className="flex items-center justify-between gap-2 rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
            >
              <span className="truncate" title={file.name}>
                {file.name}
              </span>
              <button
                type="button"
                className="shrink-0 rounded p-0.5 hover:bg-gray-100"
                aria-label={`Remove ${file.name}`}
                disabled={disabled}
                onClick={() => onFilesChange(files.filter((_, i) => i !== index))}
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function FilePicker({
  label,
  accept,
  file,
  disabled,
  inputRef,
  onFileChange,
}: {
  label: string
  accept: string
  file: File | null
  disabled?: boolean
  inputRef?: React.RefObject<HTMLInputElement | null>
  onFileChange: (file: File | null) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <label className="btn btn-secondary inline-flex w-fit cursor-pointer items-center gap-2 text-sm">
        <CloudArrowUpIcon className="h-4 w-4" />
        Choose file
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="sr-only"
          disabled={disabled}
          onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
        />
      </label>
      {file ? (
        <div className="flex max-w-[16rem] items-center gap-1 text-xs text-emerald-700">
          <span className="truncate" title={file.name}>
            {file.name}
          </span>
          <button
            type="button"
            className="shrink-0 rounded p-0.5 hover:bg-emerald-50"
            aria-label="Remove file"
            disabled={disabled}
            onClick={() => {
              onFileChange(null)
              if (inputRef?.current) inputRef.current.value = ''
            }}
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}
    </div>
  )
}

function ManageUploadedFilesView({
  disabled,
  whInvoices,
  productInvoices,
  billingMonths,
  onDeleteInvoice,
  onDeleteBillingMonth,
}: {
  disabled?: boolean
  whInvoices: WarehouseInvoiceSummary[]
  productInvoices: WarehouseInvoiceSummary[]
  billingMonths: { report_month: string; source_filename: string | null }[]
  onDeleteInvoice: (invoiceId: number) => Promise<void>
  onDeleteBillingMonth: (reportMonth: string) => Promise<void>
}) {
  return (
    <div className="max-h-[min(28rem,60vh)] space-y-5 overflow-y-auto pr-1">
      <ManageUploadedFilesSection
        title="Warehouse invoices"
        emptyMessage="No warehouse service invoices uploaded yet."
        itemCount={whInvoices.length}
        disabled={disabled}
      >
        {whInvoices.map((invoice) => (
          <ManageUploadedFilesRow
            key={invoice.id}
            primary={invoice.source_filename || invoice.invoice_number}
            secondary={[
              invoice.report_month ? `Month: ${invoice.report_month}` : null,
              `Uploaded: ${formatUploadedAt(invoice.uploaded_at)}`,
            ]}
            disabled={disabled}
            deleteLabel={`Delete warehouse invoice ${invoice.source_filename || invoice.invoice_number}`}
            onDelete={() => onDeleteInvoice(invoice.id)}
          />
        ))}
      </ManageUploadedFilesSection>

      <ManageUploadedFilesSection
        title="Product invoices"
        emptyMessage="No product invoices uploaded yet."
        itemCount={productInvoices.length}
        disabled={disabled}
      >
        {productInvoices.map((invoice) => (
          <ManageUploadedFilesRow
            key={invoice.id}
            primary={invoice.source_filename || invoice.invoice_number}
            secondary={[
              invoice.vendor_name ? `Vendor: ${invoice.vendor_name}` : null,
              invoice.invoice_date
                ? `Date: ${invoice.invoice_date}`
                : invoice.report_month
                  ? `Month: ${invoice.report_month}`
                  : null,
              `Uploaded: ${formatUploadedAt(invoice.uploaded_at)}`,
            ]}
            disabled={disabled}
            deleteLabel={`Delete product invoice ${invoice.source_filename || invoice.invoice_number}`}
            onDelete={() => onDeleteInvoice(invoice.id)}
          />
        ))}
      </ManageUploadedFilesSection>

      <ManageUploadedFilesSection
        title="Monthly billing files"
        emptyMessage="No billing files uploaded yet."
        itemCount={billingMonths.length}
        disabled={disabled}
      >
        {billingMonths.map((entry) => (
          <ManageUploadedFilesRow
            key={entry.report_month}
            primary={entry.source_filename || entry.report_month}
            secondary={[`Month: ${entry.report_month}`]}
            disabled={disabled}
            deleteLabel={`Delete billing file for ${entry.report_month}`}
            onDelete={() => onDeleteBillingMonth(entry.report_month)}
          />
        ))}
      </ManageUploadedFilesSection>
    </div>
  )
}

function ManageUploadedFilesSection({
  title,
  emptyMessage,
  itemCount,
  children,
}: {
  title: string
  emptyMessage: string
  itemCount: number
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <section>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h4>
      {itemCount > 0 ? (
        <ul className="mt-2 space-y-2">{children}</ul>
      ) : (
        <p className="mt-2 text-sm text-gray-500">{emptyMessage}</p>
      )}
    </section>
  )
}

function ManageUploadedFilesRow({
  primary,
  secondary,
  disabled,
  deleteLabel,
  onDelete,
}: {
  primary: string
  secondary: (string | null | undefined)[]
  disabled?: boolean
  deleteLabel: string
  onDelete: () => void
}) {
  const details = secondary.filter(Boolean)

  return (
    <li className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-900" title={primary}>
          {primary}
        </p>
        {details.length > 0 ? (
          <p className="mt-0.5 text-xs text-gray-500">{details.join(' · ')}</p>
        ) : null}
      </div>
      <button
        type="button"
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        aria-label={deleteLabel}
        disabled={disabled}
        onClick={() => void onDelete()}
      >
        <MinusIcon className="h-4 w-4" />
      </button>
    </li>
  )
}

function UploadModal({
  title,
  children,
  submitLabel,
  submitDisabled,
  showSubmit = true,
  wide = false,
  onClose,
  onBack,
  onSubmit,
}: {
  title: string
  children: ReactNode
  submitLabel: string
  submitDisabled?: boolean
  showSubmit?: boolean
  wide?: boolean
  onClose: () => void
  onBack?: () => void
  onSubmit: () => void
}) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className={`relative w-full rounded-xl bg-white p-5 shadow-xl ${
          wide ? 'max-w-2xl' : 'max-w-lg'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2">
          {onBack ? (
            <button
              type="button"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-gray-200 text-gray-700 hover:bg-gray-50"
              aria-label="Back"
              onClick={onBack}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
          ) : null}
          <h3 className="min-w-0 flex-1 text-sm font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="mt-4 space-y-3">{children}</div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn btn-secondary text-sm" onClick={onClose}>
            Cancel
          </button>
          {showSubmit ? (
            <button
              type="button"
              className="btn btn-primary text-sm"
              disabled={submitDisabled}
              onClick={onSubmit}
            >
              {submitLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
